"use client";

import { Link, usePathname } from "@/i18n/navigation";
import { Home, LucideProps, Target, User, Users } from "lucide-react";
import { useTranslations } from "next-intl";
import React from "react";

interface NavItem {
  name: string;
  href: string;
  icon: React.ForwardRefExoticComponent<Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>>;
}

const items: NavItem[] = [
  { name: "home",    href: "/dashboard",         icon: Home   },
  { name: "account", href: "/dashboard/account", icon: User   },
  { name: "rounds",  href: "/dashboard/rounds",  icon: Target },
  { name: "friends", href: "/dashboard/friends", icon: Users  },
];

export default function Menu() {
  const t = useTranslations("dashboard.menu");
  const pathname = usePathname();

  function isActive(href: string) {
    if (href === "/dashboard") return pathname === "/dashboard";
    return pathname.startsWith(href);
  }

  return (
    <>
      {/* -- Desktop sidebar ------------------------------------------- */}
      <nav className="fixed top-14 bottom-0 left-0 z-20 flex w-14 md:w-44 border-r border-r-border bg-primary portrait:hidden">
        <div className="flex h-full w-full flex-col pt-4 gap-1 px-2">
          {items.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                href={item.href}
                className={[
                  "group relative flex items-center justify-center md:justify-start gap-3 rounded-lg px-2 py-2.5 transition-all duration-200",
                  active
                    ? "bg-surface text-accent-1"
                    : "text-gray-400 hover:bg-surface/60 hover:text-primary-on",
                ].join(" ")}
              >
                {/* active indicator bar */}
                {active && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-r-full bg-accent-1 animate-fade-in" />
                )}

                {React.createElement(item.icon as React.ElementType, {
                  className: [
                    "w-5 h-5 shrink-0 transition-colors duration-200",
                    active ? "text-accent-1" : "text-current",
                  ].join(" "),
                })}

                <span className={[
                  "hidden md:inline text-sm font-medium transition-colors duration-200",
                  active ? "text-primary-on" : "",
                ].join(" ")}>
                  {t(item.name)}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* -- Bottom nav (portrait / mobile) ---------------------------- */}
      <nav className="fixed right-0 bottom-0 left-0 z-20 flex justify-around border-t border-t-border bg-secondary px-2 py-1 portrait:flex md:hidden landscape:hidden">
        {items.map((item) => {
          const active = isActive(item.href);
          return (
            <Link
              key={item.name}
              href={item.href}
              className={[
                "relative flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-all duration-200",
                active ? "text-accent-1" : "text-gray-500",
              ].join(" ")}
            >
              {/* pill indicator */}
              {active && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-0.5 rounded-full bg-accent-1 animate-fade-in" />
              )}

              {React.createElement(item.icon as React.ElementType, {
                className: "w-5 h-5 shrink-0",
              })}

              <span className={[
                "text-xs transition-all duration-200",
                active ? "font-semibold" : "font-normal",
              ].join(" ")}>
                {t(item.name)}
              </span>
            </Link>
          );
        })}
      </nav>
    </>
  );
}
